import clsx from 'clsx';
import { Fragment } from 'react';

import { AccuracyOverTime, AccuracyPoint } from '~/modules/game-engine/game-state/helpers/accuracy-over-time';
import { NoteQuality } from '~/modules/game-engine/game-state/helpers/calculate-note-stats';
import { PlayerNumber } from '~/modules/players/player-number';
import { GRADE_COLOURS } from '~/modules/user-profiles/grade-colours';
import { Grade } from '~/modules/user-profiles/profile-stats';
import { formatter } from '~/routes/game/singing/game-overlay/components/score-text';
import { ofFrame } from '~/routes/game/singing/game-overlay/helpers/frame-scale';
import { PlayerScore } from '~/routes/game/singing/post-game/post-game-view';
import { GRAPH_SWEEP_MS } from '~/routes/game/singing/post-game/views/results/reveal-timeline';

/**
 * The four judgements, in the order the frame lists them and with its colours - which are the
 * Tailwind palette entries the live feedback already uses. The timeline colours its line off the
 * same map, so the counts really are its key rather than merely resembling one.
 */
const JUDGEMENT_COLOURS: Record<NoteQuality, string> = {
  perfect: '#fde047',
  good: '#4ade80',
  ok: '#60a5fa',
  missed: '#f87171',
};

const JUDGEMENTS: NoteQuality[] = ['perfect', 'good', 'ok', 'missed'];

// F4b settles how a co-op round says it has no per-singer notes: an em dash with these three words
// under it, on each stat that has nothing to show. The same wording `notesUnavailableReason` gives
// the profile page, so the two screens explain it the same way.
const COOP_ONLY = 'co-op only';

/**
 * Every size the frame states in pixels, as a share of the screen's height - so the panel renders at
 * exactly the drawn pixels on a 1080 TV and shrinks below it. Four of these have to fit two deep,
 * and at fixed pixels the second row falls off a 720p screen.
 *
 * The 2x2 column is tighter than the frame's, and deliberately. The frame draws this screen with a
 * one-line tip and nothing under it; the real one carries a two-line tip band, the background-music
 * credit and the keyboard hint strip, which is some 90px the frame never had to find. Taken out of
 * the panel padding, the gaps and the grade ring, that leaves the error graph the 40px decision 7a
 * asks for; taken out of the graph, there is no graph.
 */
const pick = (compact: boolean, dense: boolean, [regular, small, smallest]: [number, number, number]) =>
  ofFrame(dense ? smallest : compact ? small : regular);

// `dense` is five to eight singers, four across and two deep: half the width of a 2x2 panel.
const sizes = (compact: boolean, dense: boolean) => ({
  pad: pick(compact, dense, [24, 16, 12]),
  gap: pick(compact, dense, [18, 8, 6]),
  name: pick(compact, dense, [28, 24, 20]),
  score: pick(compact, dense, [60, 52, 40]),
  ring: pick(compact, dense, [130, 92, 64]),
  ringText: pick(compact, dense, [78, 56, 38]),
  statPad: pick(compact, dense, [14, 6, 4]),
  stat: pick(compact, dense, [36, 30, 24]),
  statNote: pick(compact, dense, [14, 13, 12]),
  judgement: pick(compact, dense, [30, 26, 20]),
  judgementGap: pick(compact, dense, [10, 4, 2]),
  graph: pick(compact, dense, [80, 56, 40]),
  graphLine: pick(compact, dense, [3.5, 3, 2.5]),
  graphRule: ofFrame(1.5),
});

interface Props {
  player: Pick<PlayerScore, 'name' | 'notes'>;
  playerNumber: PlayerNumber;
  /** The count-up's current value, not the final score - the panel is drawn while it is still running. */
  score: number;
  grade: Grade;
  /** Colour bars down the left of the name. One per singer, so a co-op team shows all of them. */
  colours: string[];
  /** Absent when there is nobody to rank against. */
  rank?: string;
  isHighScore: boolean;
  /** Co-op scores one team rather than each singer, so it has no per-singer notes to report. */
  isCoop: boolean;
  /** How the accuracy moved through the song. Absent for the same reason `notes` is. */
  series?: AccuracyOverTime;
  compact: boolean;
  /** Five to eight panels on one screen: the judgements lose their words to fit on one line. */
  dense?: boolean;
  className?: string;
  revealGrade: boolean;
  revealStats: boolean;
  revealJudgements: boolean;
  revealGraph: boolean;
}

export default function SingerPanel({
  player,
  playerNumber,
  score,
  grade,
  colours,
  rank,
  isHighScore,
  isCoop,
  series,
  compact,
  dense = false,
  className,
  revealGrade,
  revealStats,
  revealJudgements,
  revealGraph,
}: Props) {
  const s = sizes(compact, dense);
  const gradeColour = GRADE_COLOURS[grade];
  const notes = player.notes;

  return (
    <div
      className={clsx(
        'flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/55',
        className,
      )}
      style={{ padding: s.pad, gap: s.gap }}
      data-test={`player-${playerNumber}-panel`}>
      <div className="flex shrink-0 items-center gap-[0.875rem]">
        {colours.map((colour) => (
          <span
            key={colour}
            className="shrink-0 rounded"
            style={{ width: ofFrame(8), height: ofFrame(30), background: colour }}
          />
        ))}
        <span
          className="ph-no-capture stroke-text min-w-0 truncate font-bold tracking-[.03em] uppercase"
          style={{ fontSize: s.name, color: isCoop ? '#fff' : colours[0] }}
          data-test={`player-${playerNumber}-name`}>
          {player.name}
        </span>
        {rank && (
          <span
            className={clsx(
              'shrink-0 font-bold tracking-[.06em] text-white/45 uppercase transition-opacity duration-500',
              revealGrade ? 'opacity-100' : 'opacity-0',
            )}
            style={{ fontSize: ofFrame(18) }}
            data-test={`player-${playerNumber}-rank`}>
            {rank}
          </span>
        )}
        {isHighScore && (
          <span
            className={clsx(
              'bg-active text-default ml-auto flex shrink-0 items-center rounded-md font-bold uppercase transition-all duration-500',
              revealGrade ? 'scale-100 opacity-100' : 'scale-75 opacity-0',
            )}
            style={{ height: ofFrame(32), paddingInline: ofFrame(12), fontSize: ofFrame(17) }}
            data-highscore="true">
            High score!
          </span>
        )}
      </div>

      <div className={clsx('flex shrink-0 items-center', dense ? 'gap-3' : 'gap-6')}>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Label>Score</Label>
          <span
            className="stroke-text font-bold text-white tabular-nums"
            style={{ fontSize: s.score, lineHeight: 1 }}
            data-test={`player-${playerNumber}-score`}
            data-score={Math.floor(score)}>
            {formatter.format(Math.floor(score))}
          </span>
        </div>
        <div
          className={clsx(
            'flex shrink-0 items-center justify-center rounded-full font-black transition-all duration-500',
            revealGrade ? 'scale-100 opacity-100' : 'scale-0 opacity-0',
          )}
          style={{
            width: s.ring,
            height: s.ring,
            fontSize: s.ringText,
            lineHeight: 1,
            color: gradeColour,
            background: `${gradeColour}1a`,
            border: `4px solid ${gradeColour}`,
            boxShadow: `0 0 40px ${gradeColour}40`,
          }}
          data-test={`player-${playerNumber}-grade`}>
          {grade}
        </div>
      </div>

      <div
        className={clsx(
          'flex shrink-0 items-center border-y border-white/10 transition-all duration-500',
          dense ? 'gap-4' : 'gap-8',
          revealStats ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
        )}
        style={{ paddingBlock: s.statPad }}>
        <div className="flex flex-col">
          <Label>Accuracy</Label>
          <span
            className="font-bold tabular-nums"
            style={{ fontSize: s.stat, lineHeight: 1.1, color: isCoop ? 'rgba(255,255,255,.35)' : gradeColour }}
            data-test={`player-${playerNumber}-accuracy`}>
            {isCoop || !notes ? '—' : `${(notes.accuracy * 100).toFixed(2)}%`}
          </span>
          {(isCoop || !notes) && (
            <span className="text-white/40 tabular-nums" style={{ fontSize: s.statNote }}>
              {isCoop ? COOP_ONLY : 'not measured this round'}
            </span>
          )}
        </div>
        <div className="flex flex-col">
          <Label>Max combo</Label>
          <span
            className="font-bold tabular-nums"
            style={{ fontSize: s.stat, lineHeight: 1.1, color: isCoop || !notes ? 'rgba(255,255,255,.35)' : '#fff' }}
            data-test={`player-${playerNumber}-combo`}>
            {isCoop || !notes ? (
              '—'
            ) : (
              <>
                {notes.maxCombo}
                <span className="opacity-70" style={{ fontSize: ofFrame(24) }}>
                  ×
                </span>
              </>
            )}
          </span>
          {isCoop && (
            <span className="text-white/40 tabular-nums" style={{ fontSize: s.statNote }}>
              {COOP_ONLY}
            </span>
          )}
        </div>
      </div>

      {!isCoop && notes && (
        <>
          <div
            className={clsx(
              'grid shrink-0 transition-all duration-500',
              dense ? 'grid-cols-4 gap-x-2' : 'grid-cols-2 gap-x-6',
              revealJudgements ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
            )}
            style={{ rowGap: s.judgementGap }}
            data-test={`player-${playerNumber}-judgements`}
            // The notes these four account for. Nothing on the panel prints it any more, and
            // without it a spec cannot tell whether they add up to the track or only to each other.
            data-total={notes.total}>
            {JUDGEMENTS.map((judgement) => (
              <div key={judgement} className="flex items-center gap-2.5" title={dense ? judgement : undefined}>
                <span
                  className="shrink-0 rounded-full"
                  style={{ width: ofFrame(12), height: ofFrame(12), background: JUDGEMENT_COLOURS[judgement] }}
                />
                <span
                  className="font-bold tabular-nums"
                  style={{
                    fontSize: s.judgement,
                    minWidth: dense ? undefined : ofFrame(76),
                    color: JUDGEMENT_COLOURS[judgement],
                  }}
                  data-test={`player-${playerNumber}-${judgement}`}>
                  {notes[judgement]}
                  <span className="opacity-70" style={{ fontSize: ofFrame(20) }}>
                    ×
                  </span>
                </span>
                {!dense && (
                  <span className="tracking-[.06em] text-white/45 uppercase" style={{ fontSize: ofFrame(17) }}>
                    {judgement}
                  </span>
                )}
              </div>
            ))}
          </div>
          {series && series.segments.length > 0 && (
            <AccuracyTimeline
              series={series}
              playerNumber={playerNumber}
              height={s.graph}
              line={s.graphLine}
              rule={s.graphRule}
              revealed={revealGraph}
              data-test={`player-${playerNumber}-timeline-graph`}
            />
          )}
        </>
      )}
    </div>
  );
}

const Label = ({ children }: { children: React.ReactNode }) => (
  <span className="tracking-[.08em] text-white/40 uppercase" style={{ fontSize: ofFrame(15) }}>
    {children}
  </span>
);

const PLOT_WIDTH = 1000;
const PLOT_HEIGHT = 100;

// Room above and below for a stroke of its own width, so a song sung perfectly is not drawn half
// outside its own box.
const PLOT_INSET = 6;

// Under a fifth, the difference between bad and worse is not worth the height it takes to draw: a
// line lying on the floor in red has already said what there is to say.
const AXIS_FLOOR = 0.2;

// Where a windowed value stops being one judgement and starts being the next. Higher than the note
// bands themselves - a window is an average of eight notes, so holding one above .93 means nearly
// all of them were perfect, where a single note only has to be perfect once.
const bandOf = (value: number): NoteQuality => {
  if (value >= 0.93) return 'perfect';
  if (value >= 0.75) return 'good';

  return value >= 0.45 ? 'ok' : 'missed';
};

const plotX = (at: number) => at * PLOT_WIDTH;

const plotY = (value: number) => {
  const usable = PLOT_HEIGHT - 2 * PLOT_INSET;
  const share = Math.min(1, Math.max(0, (value - AXIS_FLOOR) / (1 - AXIS_FLOOR)));

  return (1 - share) * usable + PLOT_INSET;
};

interface SegmentPath {
  line: string;
  area: string;
  durationMs: number;
  delayMs: number;
}

/**
 * One stretch of singing as a path, smoothed with Catmull-Rom written out as cubic béziers: the
 * control points either side of a point follow the line through its neighbours, which is what turns
 * a note-by-note series into a shape. Drawn as a raw polyline the same data reads as jitter at the
 * height four panels leave it.
 */
const segmentPath = (points: AccuracyPoint[]): SegmentPath => {
  const plotted = points.map((point) => ({ x: plotX(point.at), y: plotY(point.value) }));
  const round = (value: number) => value.toFixed(1);

  let line = `M${round(plotted[0].x)},${round(plotted[0].y)}`;

  for (let index = 0; index < plotted.length - 1; index++) {
    const before = plotted[index - 1] ?? plotted[index];
    const from = plotted[index];
    const to = plotted[index + 1];
    const after = plotted[index + 2] ?? plotted[index + 1];

    line +=
      `C${round(from.x + (to.x - before.x) / 6)},${round(from.y + (to.y - before.y) / 6)}` +
      ` ${round(to.x - (after.x - from.x) / 6)},${round(to.y - (after.y - from.y) / 6)}` +
      ` ${round(to.x)},${round(to.y)}`;
  }

  const first = plotted[0].x;
  const last = plotted[plotted.length - 1].x;

  return {
    line,
    area: `${line}L${round(last)},${PLOT_HEIGHT}L${round(first)},${PLOT_HEIGHT}Z`,
    // Its own share of a single sweep across the plot, rather than a sweep each: a line broken by an
    // instrumental has to draw as one pass, or the second half arrives long after the first.
    durationMs: Math.max(120, Math.round(((last - first) / PLOT_WIDTH) * GRAPH_SWEEP_MS)),
    delayMs: Math.round((first / PLOT_WIDTH) * GRAPH_SWEEP_MS),
  };
};

interface TimelineProps {
  series: AccuracyOverTime;
  playerNumber: PlayerNumber;
  height: string;
  line: string;
  rule: string;
  revealed: boolean;
  'data-test': string;
}

/**
 * How accurately the singing went as the song went on: a rolling window of the same weighted mark
 * the accuracy is counted on, left to right in song time, coloured along its length by the band each
 * value sits in. Height and colour are read as one thing, which is how it carries what was hit as
 * well as how well without a second series to read.
 *
 * Nothing is labelled. The distribution this replaced earned its `flat` and `sharp` because left and
 * right meant something a singer would not guess; here they are the start and the end of the song
 * they have just sung.
 *
 * The dashed rule is their own final accuracy - the figure printed two rows above - which turns a
 * number nobody can estimate into a comparison anybody can: above the rule beat their own average.
 */
function AccuracyTimeline({
  series,
  playerNumber,
  height,
  line,
  rule,
  revealed,
  'data-test': dataTest,
}: TimelineProps) {
  const segments = series.segments.map(segmentPath);
  const average = plotY(series.average);
  // One gradient for the whole panel rather than one per stretch, so what colour a value takes does
  // not depend on which side of an instrumental it fell. Keyed by singer: four of these share a page.
  const gradientId = `accuracy-timeline-${playerNumber}`;

  return (
    <div
      className={clsx(
        // `mt-auto` so the graph sits at the panel's foot however tall the panel is; `min-h-0` and
        // `overflow-hidden` so it is the one part that gives way when four have to fit two deep.
        'mt-auto min-h-0 overflow-hidden px-2 transition-opacity duration-500',
        revealed ? 'opacity-100' : 'opacity-0',
      )}
      style={{ height, maxHeight: height }}
      data-test={dataTest}>
      <svg
        viewBox={`0 0 ${PLOT_WIDTH} ${PLOT_HEIGHT}`}
        // The plot stretches to whatever width the panel has; every stroke below carries
        // `non-scaling-stroke` so that squash cannot thin the line at one singer count and not another.
        preserveAspectRatio="none"
        className="block h-full w-full">
        <defs>
          <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2={PLOT_WIDTH} y2="0">
            {series.segments.flat().map((point, index) => (
              <stop
                key={index}
                offset={`${(point.at * 100).toFixed(1)}%`}
                stopColor={JUDGEMENT_COLOURS[bandOf(point.value)]}
              />
            ))}
          </linearGradient>
        </defs>
        <line
          x1="0"
          y1={average}
          x2={PLOT_WIDTH}
          y2={average}
          stroke="rgba(255,255,255,.18)"
          strokeDasharray="7 9"
          vectorEffect="non-scaling-stroke"
          style={{ strokeWidth: rule }}
        />
        {segments.map((segment, index) => (
          <Fragment key={index}>
            <path d={segment.area} fill={`url(#${gradientId})`} opacity=".13" />
            {/* Drawn left to right by running the dash off the end of the path - one transition,
                which is what keeps this off the list of things that animate on a loop. */}
            <path
              d={segment.line}
              fill="none"
              stroke={`url(#${gradientId})`}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              pathLength={1}
              strokeDasharray="1"
              strokeDashoffset={revealed ? 0 : 1}
              style={{
                strokeWidth: line,
                transition: `stroke-dashoffset ${segment.durationMs}ms linear ${segment.delayMs}ms`,
              }}
            />
          </Fragment>
        ))}
      </svg>
    </div>
  );
}
